import React, { useState, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const NAME_KEY = 'sn-elearning-student-name';
const STARTED_KEY = 'sn-elearning-started';

export function LandingPage({ onStart }) {
  const [nameInput, setNameInput] = useState('');
  const [showNameError, setShowNameError] = useState(false);
  const nameRef = useRef(null);

  const handleStart = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) {
      setShowNameError(true);
      nameRef.current?.focus();
      return;
    }
    localStorage.setItem(NAME_KEY, trimmed);
    localStorage.setItem(STARTED_KEY, 'true');
    onStart();
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="landing-page">
      {/* Top accent line */}
      <div className="h-1.5 bg-gradient-to-r from-orange-500 via-amber-500 to-teal-500" />

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg text-center">
          {/* Large prominent logos */}
          <div className="flex items-center justify-center gap-6 mb-12">
            <img
              src="/steadfast-logo.png"
              alt="Steadfast"
              className="h-20 md:h-24 w-auto"
              data-testid="landing-steadfast-logo"
            />
            <div className="w-px h-16 bg-zinc-200" />
            <img
              src="/servicenow-logo.png"
              alt="ServiceNow"
              className="h-12 md:h-14 w-auto"
              data-testid="landing-servicenow-logo"
            />
          </div>

          {/* Title */}
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-orange-500 mb-3">
            Professional Training Program
          </p>
          <h1
            data-testid="landing-title"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 tracking-tight leading-tight mb-4"
          >
            ServiceNow Administration
            <br />
            <span className="text-zinc-400">Fundamentals</span>
          </h1>
          <p className="text-base text-zinc-500 leading-relaxed mb-10 max-w-md mx-auto">
            Master the ServiceNow platform from navigation to advanced administration. 
            Complete all modules to earn your certificate of completion.
          </p>

          {/* Name input card */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8 max-w-sm mx-auto">
            <p className="text-sm font-medium text-zinc-700 mb-1">
              Enter your full name to begin
            </p>
            <p className="text-xs text-zinc-400 mb-5">
              This name will be printed on your certificate and cannot be changed later.
            </p>

            <Input
              ref={nameRef}
              data-testid="landing-name-input"
              placeholder="Your full name"
              value={nameInput}
              onChange={(e) => { setNameInput(e.target.value); setShowNameError(false); }}
              onKeyDown={(e) => e.key === 'Enter' && handleStart()}
              className="h-12 text-base mb-3 border-zinc-300 focus:border-orange-500 focus:ring-orange-500/20"
            />
            {showNameError && (
              <p className="text-xs text-red-500 mb-3" data-testid="name-error">
                Please enter your full name to continue
              </p>
            )}

            <Button
              data-testid="start-training-btn"
              onClick={handleStart}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-base rounded-xl"
            >
              Start Training
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          <p className="text-[11px] text-zinc-400 mt-6">
            Powered by Steadfast IT Consulting
          </p>
        </div>
      </div>
    </div>
  );
}

export function hasStarted() {
  try { return localStorage.getItem(STARTED_KEY) === 'true'; } catch { return false; }
}

export function getStudentName() {
  try { return localStorage.getItem(NAME_KEY) || ''; } catch { return ''; }
}
